import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReadingPage, ReadingQuestion } from '@/features/reading/types';
import {
  getChapterReview,
  submitChapterReview,
  type ChapterReviewResponse,
  type ChapterReviewSubmitAnswerItem,
  type ChapterReviewNovelSection,
} from '../api/chapterReview';

export interface UseChapterReviewState {
  loading: boolean;
  error: string | null;
  data: ChapterReviewResponse | null;
  summaryPages: ReadingPage[];
  questions: ReadingQuestion[];
  isNovelReview: boolean;
  novelSections: ChapterReviewNovelSection[];
}

export interface UseChapterReviewReturn extends UseChapterReviewState {
  refresh: () => Promise<void>;
  submit: (answers: { question_id: string; selected_index: number; time_spent?: number }[]) => Promise<{
    success: boolean;
    message: string;
    accuracy_percentage: number;
    total_time_spent: string;
    correct_answers: number;
    total_questions: number;
  }>;
}

export const useChapterReview = (chapterId: number): UseChapterReviewReturn => {
  const [state, setState] = useState<UseChapterReviewState>({
    loading: true,
    error: null,
    data: null,
    summaryPages: [],
    questions: [],
    isNovelReview: false,
    novelSections: [],
  });

  const questionSectionMapRef = useRef<Map<string, number>>(new Map());
  const questionOptionIdsRef = useRef<Map<string, number[]>>(new Map());

  const splitSummaryToPages = useCallback((sectionId: number, content: string): ReadingPage[] => {
    if (!content || !content.trim()) return [];
    const paragraphs = content
      .split(/\n\s*\n+/)
      .map((p) => p.trim())
      .filter(Boolean);

    const pages: ReadingPage[] = [];
    const MAX_CHARS = 600;
    const MAX_PARAS = 3;
    let buffer: string[] = [];
    let bufferLen = 0;

    const flush = () => {
      if (buffer.length === 0) return;
      const text = buffer.join('\n\n');
      pages.push({ id: `summary-${sectionId}-${pages.length + 1}`, pageNumber: pages.length + 1, content: text });
      buffer = [];
      bufferLen = 0;
    };

    for (const para of paragraphs) {
      const nextLen = bufferLen + (buffer.length > 0 ? 2 : 0) + para.length;
      if (buffer.length >= MAX_PARAS || nextLen > MAX_CHARS) flush();
      buffer.push(para);
      bufferLen += (buffer.length > 1 ? 2 : 0) + para.length;
    }

    flush();
    return pages.length > 0 ? pages : [{ id: `summary-${sectionId}-1`, pageNumber: 1, content }];
  }, []);

  const convertQuestions = useCallback((resp: ChapterReviewResponse): ReadingQuestion[] => {
    questionSectionMapRef.current.clear();
    questionOptionIdsRef.current.clear();

    return (resp.questions || []).map((q) => {
      questionSectionMapRef.current.set(q.id, q.section_id);
      const optionIds = q.options?.map((o) => o.option_id) || [];
      questionOptionIdsRef.current.set(q.id, optionIds);

      const correctAnswerIndex = optionIds.findIndex((id) => id === q.correct_option_id);

      return {
        id: q.id,
        question: q.text,
        options: (q.options || []).map((o) => o.text),
        correctAnswer: correctAnswerIndex >= 0 ? correctAnswerIndex : -1,
        difficulty: q.difficulty,
      };
    });
  }, []);

  const refresh = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const data = await getChapterReview(chapterId);
      const novelSections = (data.sections || []).filter(
        (s): s is ChapterReviewNovelSection =>
          (s as ChapterReviewNovelSection).section_type === 'novel' ||
          !!(s as ChapterReviewNovelSection).content?.paragraphs
      );
      const isNovelReview = novelSections.length > 0;

      const pages: ReadingPage[] = [];
      if (!isNovelReview) {
        for (const s of data.sections || []) {
          const section = s as { section_id: number; summary?: string };
          pages.push(...splitSummaryToPages(section.section_id, section.summary || ''));
        }
      }

      const qs = convertQuestions(data);
      setState({
        loading: false,
        error: null,
        data,
        summaryPages: pages,
        questions: qs,
        isNovelReview,
        novelSections,
      });
    } catch (e: any) {
      setState({
        loading: false,
        error: e?.message || 'خطا در دریافت مرور فصل',
        data: null,
        summaryPages: [],
        questions: [],
        isNovelReview: false,
        novelSections: [],
      });
    }
  }, [chapterId, convertQuestions, splitSummaryToPages]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const submit = useCallback(async (answers: { question_id: string; selected_index: number; time_spent?: number }[]) => {
    try {
      const payloadAnswers: ChapterReviewSubmitAnswerItem[] = answers.map((a) => {
        const section_id = questionSectionMapRef.current.get(a.question_id) || 0;
        const optionIds = questionOptionIdsRef.current.get(a.question_id) || [];
        const idx0 = Math.max(0, (a.selected_index || 1) - 1);
        const selected_option_id = optionIds[idx0] ?? a.selected_index;
        return { section_id, question_id: a.question_id, selected_option_id, time_spent: a.time_spent };
      });

      return await submitChapterReview({ answers: payloadAnswers });
    } catch (e: any) {
      throw new Error(e?.message || 'خطا در ارسال مرور فصل');
    }
  }, []);

  return {
    ...state,
    refresh,
    submit,
  };
};
